/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'

// Components
import {
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    Text,

    // Hooks
    useStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import {ImageTextTile} from '@consteed/overrides/page-designer/assets/image-text-tile'
// Icons
import {ChevronDownIcon, ChevronRightIcon} from '@consteed/overrides/components/icons'

/**
 * The nested accordion allows you to create, as the name suggest, a nests
 * accordion given a hierarchical data structure. Each nests accordion will
 * be indented to further enhance the hierary view.
 */
const NestedAccordion = (props) => {
    const styles = useStyleConfig('NestedAccordion')
    const {
        item,
        initialDepth = 0,
        itemsFilter = () => true,
        itemsAfter,
        itemsKey = 'items',
        urlBuilder = (item) => `/${item.id}`,
        activeLink,
        ...rest
    } = props

    const depth = initialDepth
    const items = item[itemsKey] || []

    // Handle filters in the folr of a function or a object key string.
    const filter = (item) =>
        typeof itemsFilter === 'function' ? itemsFilter(item) : !!item[itemsFilter]

    return (
        <Accordion className="sf-nested-accordion" {...rest}>
            {items.filter(filter).map((item, itemIndex) => {
                const {id, name} = item
                const items = item[itemsKey]
                const key = itemIndex + '_' + id

                return (
                    <AccordionItem key={key} border="none">
                        {({isExpanded}) => (
                            <>
                                {/* Show item as a leaf node if it has no visible child items. */}
                                {items && items.filter(filter).length > 0 ? (
                                    <AccordionButton {...styles.internalButton}>
                                        {/* Replace default expanded/collapsed icons. */}
                                        {isExpanded ? (
                                            <ChevronDownIcon {...styles.internalButtonIcon} />
                                        ) : (
                                            <ChevronRightIcon {...styles.internalButtonIcon} />
                                        )}

                                        <Text textStyle={'bookLarge'}>{name}</Text>
                                    </AccordionButton>
                                ) : (
                                    <AccordionButton
                                        {...styles.leafButton}
                                        as={Link}
                                        to={urlBuilder(item)}
                                        {...(id === activeLink ? styles.leafButtonActive : {})}
                                    >
                                        {item?.image ? (
                                            <ImageTextTile {...item} />
                                        ) : (
                                            <Text textStyle={'bookLarge'}>{name}</Text>
                                        )}
                                    </AccordionButton>
                                )}

                                {/* Child Items */}
                                {items && (
                                    <AccordionPanel {...styles.panel}>
                                        <NestedAccordion
                                            {...styles.nestedAccordion}
                                            {...props}
                                            item={item}
                                            initialDepth={depth + 1}
                                        />
                                    </AccordionPanel>
                                )}
                            </>
                        )}
                    </AccordionItem>
                )
            })}

            {/* Optional accordion items after others in items list.  */}
            {typeof itemsAfter === 'function' ? itemsAfter({item, depth}) : itemsAfter}
        </Accordion>
    )
}

NestedAccordion.displayName = 'NestedAccordion'

NestedAccordion.propTypes = {
    /**
     * A POJO consistening of an id, name, and items array of object with
     * similarly specified properties.
     */
    item: PropTypes.object.isRequired,
    /**
     * An array of `AccordionItem` components that will be displayed after all
     * of the child items. Alternatively you can pass a function that will recieve
     * the current item and it's depth, your should return an `AccordionItem` or
     * array of `AccordionItem`'s.
     */
    itemsAfter: PropTypes.oneOfType([PropTypes.array, PropTypes.func]),
    /**
     * This is an internally used property used to pass the updated depth of the
     * child accordion. This is used to access specfic styl data for accodions
     * based on their depth.
     */
    initialDepth: PropTypes.number,
    /**
     * By default child items are keyed at `items` but if your data differs you
     * can specify a custom key name for chile items. (e.g. children)
     */
    itemsKey: PropTypes.string,
    /**
     * Programatically filter out items that you do not want to show. You can do this by
     * supplying a string that will be used to access an items value, the the value is truthy
     * the item will be displayed. Otherwise you can pass a function, this function will be passed
     * the item to be filtered, its return is expected to be true or false.
     */
    itemsFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    /**
     * This function builds the urls for leaf items. It accepts
     * the current item, and returns a string.
     */
    urlBuilder: PropTypes.func,
    /**
     * When using the nested-accordion for the mobile navigation, we need to highlight the link of the current page
     */
    activeLink: PropTypes.string
}

export default NestedAccordion
